import { useEffect, useState, useRef, useCallback, useMemo } from "react"
import { useForm, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslation } from "react-i18next"
import {
  GitCompareArrows,
  HelpCircle,
  ImageIcon,
  Maximize2,
  PencilLine,
  X,
} from "lucide-react"
import CodeMirror from "@uiw/react-codemirror"
import { markdown } from "@codemirror/lang-markdown"
import { oneDark } from "@codemirror/theme-one-dark"
import { EditorView } from "@codemirror/view"

import {
  FormLabel,
  Form,
  FormItem,
  FormControl,
  FormMessage,
  FormField,
  Input,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Switch,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Checkbox,
} from "@f2e/ui/components/base"
import { FormErrorMessage } from "@f2e/ui/components/base-plus"

import { useStore } from "@/store"
import AITrackedTextarea from "@/components/Conversation/AITrackedTextarea"
import { AgentHistoryDropdown } from "@/pages/Workspace/components/Agent/AgentHistoryDropdown"
import { UnsavedChangesWarning } from "@/pages/Workspace/components/Agent/UnsavedChangesWarning"
import {
  Agent,
  Workspace,
  AgentType,
  AgentHistory,
  AgentLLMModelSettings,
  AgentThinkingLevel,
  DEFAULT_AGENT_RESPONSE_MODE,
  clampOutputPresentationMaxItems,
  DEFAULT_HUMAN_HANDOFF_CONFIG,
  DEFAULT_OUTPUT_PRESENTATION_POLICY,
  MAX_AI_TAKEOVER_GREETING_LENGTH,
  normalizeAiTakeoverGreeting,
  OUTPUT_PRESENTATION_KINDS,
  OutputPresentationKind,
  OutputPresentationPolicy,
  Permission,
} from "@/types"
import { useDebouncedEffect } from "@/hooks"
import type { KnowledgeBaseFolder } from "@/hooks/api"
import type { LLMModel } from "@/hooks/api/llm"
import { hasPermission } from "@/lib/utils"

import {
  BasicFormSchema,
  BasicFormType,
  MAX_ATTACHMENTS,
  MAX_PRESENTATION_GUIDANCE_LENGTH,
  MAX_SUGGESTED_QUESTIONS,
  normalizeOutputPresentationPolicy,
} from "./type"
import { UnifiedToolSelector } from "./UnifiedToolSelector"
import { SuggestedQuestionsField } from "./SuggestedQuestionsField"
import { HandoffNotificationSection } from "./HandoffNotifications/HandoffNotificationSection"
import { summarizeAttachments } from "@/components/Copilot/summarizers"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import { LLMModelSelect } from "@/components/Common/LLMModelSelect"
import { AgentHistoryDropdownState } from "@/hooks/useAgentHistories"
import { AttachmentCollector } from "@/components/Conversation/AttachmentCollector"
import {
  FileUploadCollector,
  FileUploadCollectorHandle,
} from "@/components/Conversation/FileUploadCollector"
import {
  isSuggestionApplyEventForCurrentPath,
  notifyFieldApplyValueChanged,
  registerFieldApplyEntries,
  subscribeSuggestionApplyEvents,
} from "@/components/Copilot/applySuggestion/applySuggestionHandler"
import { makeUnifiedToolsApplyHandler } from "@/components/Copilot/applySuggestion/makeUnifiedToolsApplyHandler"
import { makeLLMModelApplyHandler } from "@/components/Copilot/applySuggestion/makeLLMModelApplyHandler"
import {
  getAudioCapableModels,
  getModelDisplayName,
  getSystemDefaultTextModelId,
  isAudioAttachment,
  modelSupportsAudioInput,
} from "@/lib/modelCapabilities"

const FieldHelpTooltip = ({ message }: { message: string }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Field help"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left leading-relaxed">
        <p>{message}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const OUTPUT_PRESENTATION_PREVIEW_IMAGES: Record<
  OutputPresentationKind,
  string
> = {
  citation_list: "/output_preview_citation_list.png",
  card_carousel: "/output_preview_card_carousel.png",
  image_gallery: "/output_preview_image_gallery.png",
  action_panel: "/output_preview_action_panel.png",
  list_digest: "/output_preview_list_digest.png",
  status_timeline: "/output_preview_status_timeline.png",
  location_carousel: "/output_preview_location_carousel.png",
}

const OutputPresentationPreviewTooltip = ({
  label,
  kind,
  previewLabel,
}: {
  label: string
  kind: OutputPresentationKind
  previewLabel: string
}) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={previewLabel}
          data-testid={`output-presentation-preview-${kind}`}
        >
          <ImageIcon className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        sideOffset={6}
        className="max-w-[min(24rem,calc(100vw-2rem))] border border-border bg-popover p-2 text-popover-foreground shadow-lg"
      >
        <img
          src={OUTPUT_PRESENTATION_PREVIEW_IMAGES[kind]}
          alt={label}
          loading="lazy"
          className="max-h-[22rem] w-auto max-w-full rounded-sm object-contain"
        />
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const THINKING_OPTIONS: Array<{
  value: "__default__" | AgentThinkingLevel
  label: string
}> = [
  { value: "__default__", label: "Default" },
  { value: "minimal", label: "Minimal" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "xhigh", label: "XHigh" },
]

const BASIC_FORM_APPLY_KEY_BY_FIELD_NAME = {
  name: "basicForm.name",
  description: "basicForm.description",
  systemPrompt: "basicForm.systemPrompt",
  llmModel: "basicForm.llmModel",
  responseMode: "basicForm.responseMode",
  unifiedTools: "basicForm.unifiedTools",
} as const

const BASIC_FORM_APPLY_FIELD_NAMES = Object.keys(
  BASIC_FORM_APPLY_KEY_BY_FIELD_NAME
) as Array<keyof typeof BASIC_FORM_APPLY_KEY_BY_FIELD_NAME>

const sanitizeLLMModelSettings = (
  settings?: AgentLLMModelSettings
): AgentLLMModelSettings | undefined => {
  if (!settings) return undefined

  const sanitized: AgentLLMModelSettings = {}
  if (
    settings.temperature !== undefined &&
    Number.isFinite(settings.temperature)
  ) {
    sanitized.temperature = settings.temperature
  }
  if (settings.maxTokens !== undefined && Number.isFinite(settings.maxTokens)) {
    sanitized.maxTokens = settings.maxTokens
  }
  if (settings.topP !== undefined && Number.isFinite(settings.topP)) {
    sanitized.topP = settings.topP
  }
  if (settings.thinking !== undefined) sanitized.thinking = settings.thinking

  return Object.keys(sanitized).length > 0 ? sanitized : undefined
}

const extractLLMModelSettingsErrorMessages = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any
): string[] => {
  if (!error || typeof error !== "object") return []

  const messages = new Set<string>()
  if (typeof error.message === "string" && error.message.length > 0) {
    messages.add(error.message)
  }

  for (const [key, value] of Object.entries(error)) {
    if (key === "message") continue
    for (const message of extractLLMModelSettingsErrorMessages(value)) {
      messages.add(message)
    }
  }

  return Array.from(messages)
}

const getOutputPresentationPolicyFields = (
  source?:
    | { outputPresentationPolicy?: OutputPresentationPolicy }
    | Record<string, unknown>
) => {
  const rawPolicy =
    source &&
    "outputPresentationPolicy" in source &&
    typeof source.outputPresentationPolicy === "object"
      ? (source.outputPresentationPolicy as OutputPresentationPolicy)
      : source &&
          "output_presentation_policy" in source &&
          typeof source.output_presentation_policy === "object"
        ? ({
            enableOutputPresentations: Boolean(
              (
                source.output_presentation_policy as {
                  enable_output_presentations?: boolean
                }
              ).enable_output_presentations
            ),
            allowedPresentationKinds:
              (
                source.output_presentation_policy as {
                  allowed_presentation_kinds?: OutputPresentationKind[]
                }
              ).allowed_presentation_kinds || [],
            maxItemsByKind:
              (
                source.output_presentation_policy as {
                  max_items_by_kind?: OutputPresentationPolicy["maxItemsByKind"]
                }
              ).max_items_by_kind || {},
            whenToUseByKind:
              (
                source.output_presentation_policy as {
                  when_to_use_by_kind?: OutputPresentationPolicy["whenToUseByKind"]
                }
              ).when_to_use_by_kind || {},
          } as OutputPresentationPolicy)
        : undefined

  return {
    outputPresentationPolicy: normalizeOutputPresentationPolicy(rawPolicy),
  }
}

const sortRecordByKey = <T,>(record: Record<string, T>): Record<string, T> =>
  Object.fromEntries(
    Object.entries(record).sort(([left], [right]) => left.localeCompare(right))
  )

const normalizeOutputPresentationPolicyForCompare = (
  policy?: Partial<OutputPresentationPolicy>
) => {
  const normalized = normalizeOutputPresentationPolicy(
    policy || DEFAULT_OUTPUT_PRESENTATION_POLICY
  )

  return {
    ...normalized,
    allowedPresentationKinds: [...normalized.allowedPresentationKinds].sort(),
    maxItemsByKind: sortRecordByKey(normalized.maxItemsByKind),
    whenToUseByKind: sortRecordByKey(normalized.whenToUseByKind),
  }
}

export interface BasicFormProps {
  formId?: string
  currentAgent?: Agent
  workspace: Workspace
  knowledgeBaseMap: Map<string, KnowledgeBaseFolder>
  agents: Agent[] // Add agents prop
  initialValues?: BasicFormType
  copilotApplyEnabled?: boolean
  children?: React.ReactNode
  onConfirm?: (data: BasicFormType) => void
  historiesState?: AgentHistoryDropdownState
  onDirtyChange?: (dirty: boolean) => void
}

const getAttachmentResetSignature = (
  attachments: BasicFormType["attachments"]
) =>
  (attachments || [])
    .map((attachment) => ({
      uuid: attachment.uuid || "",
      name: attachment.file?.name || "",
      url: attachment.url || "",
      scope: attachment.scope,
      status: attachment.status,
    }))
    .sort((a, b) => `${a.uuid}:${a.name}`.localeCompare(`${b.uuid}:${b.name}`))

const normalizeHumanHandoffForCompare = (
  humanHandoff?:
    | BasicFormType["humanHandoff"]
    | Partial<NonNullable<BasicFormType["humanHandoff"]>>
): {
  enabled: boolean
  idleTimeoutMinutes: number | null
  handoffInstructions?: string
} => ({
  enabled: humanHandoff?.enabled ?? false,
  idleTimeoutMinutes:
    humanHandoff?.idleTimeoutMinutes == null
      ? null
      : humanHandoff.idleTimeoutMinutes,
  handoffInstructions: humanHandoff?.handoffInstructions || undefined,
})

const getFormResetSignature = (values: BasicFormType) =>
  JSON.stringify({
    name: values.name,
    description: values.description,
    systemPrompt: values.systemPrompt,
    unifiedTools: values.unifiedTools,
    llmModel: values.llmModel || "",
    llmModelSettings: values.llmModelSettings || {},
    attachments: getAttachmentResetSignature(values.attachments),
    suggestedQuestions: values.suggestedQuestions || [],
    responseMode: values.responseMode || DEFAULT_AGENT_RESPONSE_MODE,
    humanHandoff: normalizeHumanHandoffForCompare(
      values.humanHandoff || DEFAULT_HUMAN_HANDOFF_CONFIG
    ),
    aiTakeoverGreeting: normalizeAiTakeoverGreeting(values.aiTakeoverGreeting),
    outputPresentationPolicy: normalizeOutputPresentationPolicyForCompare(
      values.outputPresentationPolicy
    ),
    pendingKnowledgeBindings: values.pendingKnowledgeBindings || [],
  })

const buildFormValuesFromHistory = (
  history: AgentHistory,
  systemDefaultLLMModel?: string
): BasicFormType => ({
  name: history.name,
  description: history.description || "",
  systemPrompt: history.systemPrompt || "",
  unifiedTools: history.unifiedTools || [],
  llmModel:
    history.llmModel ||
    ((history.attachments || []).some((attachment) =>
      isAudioAttachment({ type: attachment.file.type })
    )
      ? ""
      : systemDefaultLLMModel || ""),
  llmModelSettings: history.llmModelSettings || {},
  attachments: history.attachments || [],
  suggestedQuestions: history.suggestedQuestions || [],
  responseMode: history.responseMode || DEFAULT_AGENT_RESPONSE_MODE,
  knowledgeNodeIds: [],
  humanHandoff: history.humanHandoff || {
    ...DEFAULT_HUMAN_HANDOFF_CONFIG,
  },
  aiTakeoverGreeting: normalizeAiTakeoverGreeting(history.aiTakeoverGreeting),
  outputPresentationPolicy: normalizeOutputPresentationPolicy(
    history.outputPresentationPolicy ||
      getOutputPresentationPolicyFields(history.meta).outputPresentationPolicy
  ),
  pendingKnowledgeBindings: [],
})

const BasicForm = ({
  formId,
  currentAgent,
  workspace,
  knowledgeBaseMap,
  agents,
  initialValues,
  copilotApplyEnabled = true,
  children,
  onConfirm,
  historiesState,
  onDirtyChange,
}: BasicFormProps) => {
  const { t } = useTranslation()
  const isInitialLoadRef = useRef(true)

  const availableModels = useStore((state) => state.availableModels)
  const sysConfig = useStore((state) => state.sysConfig)

  // File upload ref for drag-and-drop
  const fileUploadRef = useRef<FileUploadCollectorHandle>(null)

  // Get available agents for the call agent tool (excluding current agent if editing)
  // Filter agents for the current workspace, basic type, and exclude current agent
  const availableAgentsForCallAgentTool = useMemo(() => {
    return (agents || []).filter(
      (a) =>
        a.workspaceId === workspace.id &&
        a.type === AgentType.Basic &&
        a.id !== currentAgent?.id
    )
  }, [agents, workspace.id, currentAgent?.id])

  // Version management state
  const [showHistoryChangeWarning, setShowHistoryChangeWarning] =
    useState(false)
  // Toggle for inline track changes
  const [showTrackChanges, setShowTrackChanges] = useState(false)
  // Store the baseline value when tracking is enabled
  const [trackingBaselineValue, setTrackingBaselineValue] = useState<string>("")
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false)
  const systemPromptSectionRef = useRef<HTMLDivElement>(null)
  const [pendingHistorySelection, setPendingHistorySelection] =
    useState<AgentHistory | null>(null)
  const [tabularToolsEnabled, setTabularToolsEnabled] = useState(false)
  // Track which history is currently applied to avoid redundant resets
  const lastAppliedHistoryIdRef = useRef<string | null>(null)
  const lastResetSignatureRef = useRef<string | null>(null)
  const pendingSubmittedSignatureRef = useRef<string | null>(null)
  const organizationId = workspace.organizationId || ""
  const canManageHandoffNotifications = hasPermission(
    workspace.myPermissions || new Set(),
    Permission.WsUpdateResources
  )

  const systemDefaultLLMModel = useMemo(() => {
    return getSystemDefaultTextModelId(sysConfig, availableModels || [])
  }, [sysConfig, availableModels])

  const form = useForm<BasicFormType>({
    resolver: zodResolver(BasicFormSchema),
    defaultValues: {
      name: currentAgent?.name || initialValues?.name || "",
      description:
        currentAgent?.description || initialValues?.description || "",
      systemPrompt:
        currentAgent?.systemPrompt || initialValues?.systemPrompt || "",
      unifiedTools:
        currentAgent?.unifiedTools || initialValues?.unifiedTools || [],
      llmModel:
        currentAgent?.llmModel ||
        initialValues?.llmModel ||
        systemDefaultLLMModel ||
        "",
      llmModelSettings:
        currentAgent?.llmModelSettings || initialValues?.llmModelSettings || {},
      attachments: initialValues?.attachments || [],
      suggestedQuestions:
        currentAgent?.suggestedQuestions ||
        initialValues?.suggestedQuestions ||
        [],
      responseMode:
        currentAgent?.responseMode ||
        initialValues?.responseMode ||
        DEFAULT_AGENT_RESPONSE_MODE,
      humanHandoff: currentAgent?.humanHandoff ||
        initialValues?.humanHandoff || {
          ...DEFAULT_HUMAN_HANDOFF_CONFIG,
        },
      aiTakeoverGreeting: normalizeAiTakeoverGreeting(
        currentAgent
          ? currentAgent.aiTakeoverGreeting
          : initialValues?.aiTakeoverGreeting
      ),
      outputPresentationPolicy: normalizeOutputPresentationPolicy(
        currentAgent?.outputPresentationPolicy ||
          currentAgent?.meta?.outputPresentationPolicy ||
          initialValues?.outputPresentationPolicy
      ),
      pendingKnowledgeBindings: initialValues?.pendingKnowledgeBindings || [],
    },
  })

  const { getValues, setValue, control, subscribe } = form

  useEffect(
    () =>
      subscribe({
        name: BASIC_FORM_APPLY_FIELD_NAMES,
        formState: { values: true },
        exact: true,
        callback: ({ name }) => {
          if (!name) {
            Object.values(BASIC_FORM_APPLY_KEY_BY_FIELD_NAME).forEach(
              notifyFieldApplyValueChanged
            )
            return
          }

          const applyKey =
            BASIC_FORM_APPLY_KEY_BY_FIELD_NAME[
              name as keyof typeof BASIC_FORM_APPLY_KEY_BY_FIELD_NAME
            ]
          if (applyKey) notifyFieldApplyValueChanged(applyKey)
        },
      }),
    [subscribe]
  )

  const unifiedTools = useWatch({ control, name: "unifiedTools" })
  const pendingKnowledgeBindings = useWatch({
    control,
    name: "pendingKnowledgeBindings",
  })
  const llmModel = useWatch({ control, name: "llmModel" })
  const llmModelSettings = useWatch({ control, name: "llmModelSettings" })
  const isCurrentModelMissing = useMemo(() => {
    const base = availableModels || []
    const current = llmModel
    return (
      !!current &&
      typeof current === "string" &&
      !base.find((m) => m.modelId === current)
    )
  }, [availableModels, llmModel])

  const modelOptionsEnsuringCurrentIncluded = useMemo(() => {
    const base = availableModels || []
    if (isCurrentModelMissing) {
      const currentModel: LLMModel = {
        id: 0,
        modelId: llmModel || "",
        displayName: llmModel || "",
        provider: "",
        modelType: "text",
        inputModalities: ["text"],
        createdAt: "",
      }
      return [currentModel, ...base]
    }
    return base
  }, [availableModels, isCurrentModelMissing, llmModel])

  const name = useWatch({ control, name: "name" })
  const description = useWatch({ control, name: "description" })
  const systemPrompt = useWatch({ control, name: "systemPrompt" })
  const attachments = useWatch({ control, name: "attachments" })
  const suggestedQuestions = useWatch({ control, name: "suggestedQuestions" })
  const hasSystemPromptChanges = (systemPrompt || "") !== trackingBaselineValue

  useEffect(() => {
    if (!hasSystemPromptChanges) setShowTrackChanges(false)
  }, [hasSystemPromptChanges])

  useEffect(
    () =>
      subscribeSuggestionApplyEvents((event) => {
        if (
          event.phase !== "complete" ||
          !isSuggestionApplyEventForCurrentPath(event) ||
          !event.appliedKeys.includes(
            BASIC_FORM_APPLY_KEY_BY_FIELD_NAME.systemPrompt
          )
        ) {
          return
        }

        setShowTrackChanges(true)
        window.setTimeout(() => {
          systemPromptSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          })
        }, 350)
      }),
    []
  )
  const currentLLMModelSettings = useMemo(
    () => (llmModel ? llmModelSettings?.[llmModel] : undefined),
    [llmModel, llmModelSettings]
  )
  const llmModelSettingsErrorsMap = form.formState.errors.llmModelSettings as
    | Record<string, unknown>
    | undefined
  const currentLLMModelSettingsErrors = llmModel
    ? (llmModelSettingsErrorsMap?.[llmModel] ?? undefined)
    : undefined
  const otherLLMModelSettingsErrorMessages = useMemo(() => {
    if (!llmModelSettingsErrorsMap) return []

    const modelNameMap = new Map(
      modelOptionsEnsuringCurrentIncluded.map((model) => [
        model.modelId,
        model.displayName || model.modelId,
      ])
    )

    return Object.entries(llmModelSettingsErrorsMap).flatMap(
      ([modelId, error]) => {
        if (modelId === llmModel || !error) return []
        const messages = extractLLMModelSettingsErrorMessages(error)
        if (messages.length === 0) return []

        if (modelId === "root") {
          return messages
        }

        const modelLabel = modelNameMap.get(modelId) ?? modelId
        return messages.map((message) => `${modelLabel}: ${message}`)
      }
    )
  }, [llmModelSettingsErrorsMap, llmModel, modelOptionsEnsuringCurrentIncluded])
  const hasCurrentLLMModelSettings =
    !!currentLLMModelSettings && Object.keys(currentLLMModelSettings).length > 0
  const humanHandoffEnabled = useWatch({
    control,
    name: "humanHandoff.enabled",
  })
  const responseMode = useWatch({ control, name: "responseMode" })
  const humanHandoff = useWatch({ control, name: "humanHandoff" })
  const aiTakeoverGreeting = useWatch({ control, name: "aiTakeoverGreeting" })
  const hasAiTakeoverGreeting =
    normalizeAiTakeoverGreeting(aiTakeoverGreeting).length > 0
  const outputPresentationPolicy = useWatch({
    control,
    name: "outputPresentationPolicy",
  })
  const attachmentsHaveAudio = useMemo(
    () =>
      (attachments || []).some((attachment) =>
        isAudioAttachment({ type: attachment.file.type })
      ),
    [attachments]
  )
  const audioCapableModels = useMemo(
    () => getAudioCapableModels(modelOptionsEnsuringCurrentIncluded),
    [modelOptionsEnsuringCurrentIncluded]
  )
  const llmModelOptions = useMemo(() => {
    if (!attachmentsHaveAudio) {
      return modelOptionsEnsuringCurrentIncluded
    }

    const selectedModel = modelOptionsEnsuringCurrentIncluded.find(
      (model) => model.modelId === llmModel
    )
    const filteredModels = audioCapableModels
    if (
      selectedModel &&
      !filteredModels.find((model) => model.modelId === selectedModel.modelId)
    ) {
      return [selectedModel, ...filteredModels]
    }
    return filteredModels
  }, [
    attachmentsHaveAudio,
    modelOptionsEnsuringCurrentIncluded,
    audioCapableModels,
    llmModel,
  ])
  const audioAttachmentModelError = useMemo(() => {
    if (!attachmentsHaveAudio) {
      return ""
    }
    if (!llmModel || llmModel.trim() === "") {
      return t("common.audioInput.modelRequired")
    }
    if (!modelSupportsAudioInput(llmModel, availableModels || [])) {
      return t("common.audioInput.unsupportedModel", {
        model: getModelDisplayName(llmModel, availableModels || []),
      })
    }
    return ""
  }, [attachmentsHaveAudio, llmModel, t, availableModels])
  const [advancedValue, setAdvancedValue] = useState("")
  const hasAdvancedErrors = Boolean(
    form.formState.errors.llmModelSettings ||
    form.formState.errors.attachments ||
    form.formState.errors.suggestedQuestions ||
    form.formState.errors.humanHandoff ||
    form.formState.errors.aiTakeoverGreeting ||
    form.formState.errors.outputPresentationPolicy
  )
  const advancedSummary = useMemo(() => {
    const summaryItems: string[] = []

    if (hasCurrentLLMModelSettings) {
      summaryItems.push(
        t("workspace.agent.basicForm.advancedSummaryModelSettings", {
          defaultValue: "Model settings",
        })
      )
    }
    if ((attachments || []).length > 0) {
      summaryItems.push(
        t("workspace.agent.basicForm.advancedSummaryAttachments", {
          count: attachments.length,
          defaultValue: "{{count}} attachments",
        })
      )
    }
    if ((suggestedQuestions || []).length > 0) {
      summaryItems.push(
        t("workspace.agent.basicForm.advancedSummarySuggestedQuestions", {
          count: suggestedQuestions.length,
          defaultValue: "{{count}} suggested questions",
        })
      )
    }
    if (responseMode === "ai" && humanHandoff?.enabled) {
      summaryItems.push(
        t("workspace.agent.basicForm.advancedSummaryHumanHandoff", {
          defaultValue: "Human handoff",
        })
      )
    }
    if (responseMode === "ai" && hasAiTakeoverGreeting) {
      summaryItems.push(
        t("workspace.agent.basicForm.advancedSummaryAiTakeoverGreeting", {
          defaultValue: "AI takeover greeting",
        })
      )
    }
    if (responseMode === "human_only") {
      summaryItems.push(
        t("workspace.agent.basicForm.advancedSummaryHumanOnly", {
          defaultValue: "Human replies only",
        })
      )
    }
    if (outputPresentationPolicy?.enableOutputPresentations) {
      summaryItems.push(
        t("workspace.agent.basicForm.advancedSummaryOutputPresentation", {
          count: outputPresentationPolicy.allowedPresentationKinds.length,
          defaultValue: "{{count}} output presentations",
        })
      )
    }

    if (summaryItems.length === 0) {
      return t("workspace.agent.basicForm.advancedSummaryDefaults", "Defaults")
    }

    return summaryItems.join(" · ")
  }, [
    attachments,
    hasAiTakeoverGreeting,
    hasCurrentLLMModelSettings,
    humanHandoff?.enabled,
    outputPresentationPolicy,
    responseMode,
    suggestedQuestions,
    t,
  ])

  useEffect(() => {
    if (hasAdvancedErrors) {
      setAdvancedValue("advanced")
    }
  }, [hasAdvancedErrors])

  const updateCurrentLLMModelSettings = useCallback(
    (updater: (settings: AgentLLMModelSettings) => AgentLLMModelSettings) => {
      if (!llmModel) return

      const currentSettings = llmModelSettings?.[llmModel] || {}
      const nextSettings = sanitizeLLMModelSettings(updater(currentSettings))
      const nextMap = { ...(llmModelSettings || {}) }

      if (nextSettings) {
        nextMap[llmModel] = nextSettings
      } else {
        delete nextMap[llmModel]
      }

      setValue("llmModelSettings", nextMap, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    },
    [llmModel, llmModelSettings, setValue]
  )

  const updateOutputPresentationPolicy = useCallback(
    (
      updater: (policy: OutputPresentationPolicy) => OutputPresentationPolicy
    ) => {
      const current = normalizeOutputPresentationPolicy(
        outputPresentationPolicy
      )
      setValue(
        "outputPresentationPolicy",
        normalizeOutputPresentationPolicy(updater(current)),
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        }
      )
    },
    [outputPresentationPolicy, setValue]
  )

  const setOutputPresentationKindEnabled = useCallback(
    (kind: OutputPresentationKind, enabled: boolean) => {
      const option = OUTPUT_PRESENTATION_KINDS.find(
        (item) => item.kind === kind
      )
      updateOutputPresentationPolicy((policy) => {
        const allowedSet = new Set(policy.allowedPresentationKinds)
        if (enabled) {
          allowedSet.add(kind)
        } else {
          allowedSet.delete(kind)
        }

        const maxItemsByKind = { ...policy.maxItemsByKind }
        if (enabled && option && maxItemsByKind[kind] === undefined) {
          maxItemsByKind[kind] = option.defaultMaxItems
        }

        return {
          ...policy,
          allowedPresentationKinds: OUTPUT_PRESENTATION_KINDS.map(
            ({ kind: knownKind }) => knownKind
          ).filter((knownKind) => allowedSet.has(knownKind)),
          maxItemsByKind,
        }
      })
    },
    [updateOutputPresentationPolicy]
  )

  const switchToHistory = useCallback(
    (history: AgentHistory) => {
      historiesState?.setCurrentHistoryId(history.id)
      // After successfully switching, mark this history as the last applied
      lastAppliedHistoryIdRef.current = history.id

      // Create new default values based on the selected history
      const newDefaultValues = buildFormValuesFromHistory(
        history,
        systemDefaultLLMModel
      )

      // Set initial load flag to prevent triggering onChange during reset
      isInitialLoadRef.current = true

      // Update form's default values to match the new history
      form.reset(newDefaultValues)
      lastResetSignatureRef.current = getFormResetSignature(newDefaultValues)
      // Update tracking baseline to the history's system prompt
      setTrackingBaselineValue(history.systemPrompt || "")

      setTimeout(() => {
        isInitialLoadRef.current = false
      }, 0)
    },
    [historiesState, form, systemDefaultLLMModel]
  )

  const handleHistoryChangeConfirm = () => {
    if (pendingHistorySelection) {
      switchToHistory(pendingHistorySelection)
    }
    setShowHistoryChangeWarning(false)
    setPendingHistorySelection(null)
  }

  const handleHistoryChangeCancel = () => {
    setShowHistoryChangeWarning(false)
    setPendingHistorySelection(null)
  }

  // Helper function to check if values have changed

  const hasValueChanged = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (currentValue: any, defaultValue: any, useDeepComparison = false) => {
      if (useDeepComparison) {
        return (
          JSON.stringify(currentValue) !== JSON.stringify(defaultValue || [])
        )
      }
      // For arrays, compare length and content
      if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
        return (
          currentValue.length !== defaultValue.length ||
          !currentValue.every((val, index) => val === defaultValue[index])
        )
      }
      return currentValue !== defaultValue
    },
    []
  )

  // Shared function to detect if form has unsaved changes
  const checkForUnsavedChanges = useCallback(() => {
    const defaultValues = form.formState.defaultValues
    const currentValues = form.getValues()

    // Get current and  attachment IDs for comparison
    const currentAttachmentIds = currentValues?.attachments
      ?.map((att) => att.uuid as string)
      .sort()
    const defaultAttachmentIds = defaultValues?.attachments
      ?.map((att) => att?.uuid as string)
      .sort()

    return (
      hasValueChanged(currentValues.name, defaultValues?.name || "") ||
      hasValueChanged(
        currentValues.description,
        defaultValues?.description || ""
      ) ||
      hasValueChanged(
        currentValues.systemPrompt,
        defaultValues?.systemPrompt || ""
      ) ||
      hasValueChanged(
        currentValues.unifiedTools,
        defaultValues?.unifiedTools || [],
        true
      ) ||
      hasValueChanged(currentValues.llmModel, defaultValues?.llmModel || "") ||
      hasValueChanged(
        currentValues.llmModelSettings,
        defaultValues?.llmModelSettings || {},
        true
      ) ||
      hasValueChanged(currentAttachmentIds, defaultAttachmentIds) ||
      hasValueChanged(
        currentValues.suggestedQuestions,
        defaultValues?.suggestedQuestions || [],
        true
      ) ||
      hasValueChanged(
        currentValues.responseMode,
        defaultValues?.responseMode || DEFAULT_AGENT_RESPONSE_MODE
      ) ||
      hasValueChanged(
        normalizeHumanHandoffForCompare(currentValues.humanHandoff),
        normalizeHumanHandoffForCompare(
          defaultValues?.humanHandoff || DEFAULT_HUMAN_HANDOFF_CONFIG
        ),
        true
      ) ||
      hasValueChanged(
        normalizeAiTakeoverGreeting(currentValues.aiTakeoverGreeting),
        normalizeAiTakeoverGreeting(defaultValues?.aiTakeoverGreeting)
      ) ||
      hasValueChanged(
        normalizeOutputPresentationPolicyForCompare(
          currentValues.outputPresentationPolicy
        ),
        normalizeOutputPresentationPolicyForCompare(
          defaultValues?.outputPresentationPolicy as
            | Partial<OutputPresentationPolicy>
            | undefined
        ),
        true
      ) ||
      hasValueChanged(
        currentValues.pendingKnowledgeBindings || [],
        defaultValues?.pendingKnowledgeBindings || [],
        true
      )
    )
  }, [form, hasValueChanged])

  // Emit dirty status changes (single source of truth)
  const lastDirtyRef = useRef<boolean>(false)
  const dirtyWatchValues = useMemo(
    () => ({
      name,
      description,
      systemPrompt,
      llmModel,
      llmModelSettings,
      unifiedTools,
      attachmentIds: (attachments || [])
        .map((att) => att?.uuid as string)
        .sort()
        .join("|"),
      suggestedQuestions: (suggestedQuestions || []).join("|"),
      responseMode,
      humanHandoff: JSON.stringify(
        normalizeHumanHandoffForCompare(
          humanHandoff || DEFAULT_HUMAN_HANDOFF_CONFIG
        )
      ),
      aiTakeoverGreeting: normalizeAiTakeoverGreeting(aiTakeoverGreeting),
      outputPresentationPolicy: JSON.stringify(
        normalizeOutputPresentationPolicyForCompare(outputPresentationPolicy)
      ),
      pendingKnowledgeBindings: JSON.stringify(pendingKnowledgeBindings || []),
    }),
    [
      aiTakeoverGreeting,
      attachments,
      description,
      humanHandoff,
      outputPresentationPolicy,
      llmModel,
      llmModelSettings,
      name,
      systemPrompt,
      unifiedTools,
      suggestedQuestions,
      pendingKnowledgeBindings,
      responseMode,
    ]
  )
  useDebouncedEffect({
    callback: () => {
      const isDirty = checkForUnsavedChanges()
      if (isDirty !== lastDirtyRef.current) {
        lastDirtyRef.current = isDirty
        onDirtyChange?.(isDirty)
      }
    },
    delay: 150,
    deps: [
      checkForUnsavedChanges,
      dirtyWatchValues,
      form.formState.defaultValues,
    ],
    condition: () => !!form.formState.defaultValues,
  })

  // Sync form with updated currentHistory from history dropdown
  useEffect(() => {
    const history = historiesState?.currentHistory
    // Guard: no current history fetched yet
    if (!history) return

    // If this history is already applied, do nothing
    if (lastAppliedHistoryIdRef.current === history.id) return

    const historyValues = buildFormValuesFromHistory(
      history,
      systemDefaultLLMModel
    )
    const currentFormSignature = getFormResetSignature(form.getValues())
    const incomingHistorySignature = getFormResetSignature(historyValues)

    if (
      pendingSubmittedSignatureRef.current &&
      pendingSubmittedSignatureRef.current === incomingHistorySignature
    ) {
      pendingSubmittedSignatureRef.current = null
      switchToHistory(history)
      return
    }

    if (currentFormSignature === incomingHistorySignature) {
      switchToHistory(history)
      return
    }

    // Check if there are unsaved changes using the shared function
    if (checkForUnsavedChanges()) {
      setPendingHistorySelection(history)
      setShowHistoryChangeWarning(true)
      return
    }

    // No unsaved changes, proceed with history switch
    switchToHistory(history)
  }, [
    historiesState?.currentHistory,
    checkForUnsavedChanges,
    form,
    switchToHistory,
    systemDefaultLLMModel,
  ])

  // Reset form when agent prop changes
  useEffect(() => {
    isInitialLoadRef.current = true
    const templateValues = currentAgent ? undefined : initialValues

    // Get attachments and suggestedQuestions from current history if available
    const currentAttachments = currentAgent
      ? historiesState?.currentHistory?.attachments || []
      : templateValues?.attachments || []
    const currentSuggestedQuestions = currentAgent
      ? historiesState?.currentHistory?.suggestedQuestions || []
      : templateValues?.suggestedQuestions || []
    const initialSystemPrompt =
      currentAgent?.systemPrompt || templateValues?.systemPrompt || ""
    const nextResetValues: BasicFormType = {
      name: currentAgent?.name || templateValues?.name || "",
      description:
        currentAgent?.description || templateValues?.description || "",
      systemPrompt: initialSystemPrompt,
      unifiedTools:
        currentAgent?.unifiedTools || templateValues?.unifiedTools || [],
      knowledgeNodeIds: [],
      llmModel:
        currentAgent?.llmModel ||
        templateValues?.llmModel ||
        (currentAttachments.some((attachment) =>
          isAudioAttachment({ type: attachment.file.type })
        )
          ? ""
          : systemDefaultLLMModel || ""),
      llmModelSettings:
        currentAgent?.llmModelSettings ||
        templateValues?.llmModelSettings ||
        {},
      attachments: currentAttachments,
      suggestedQuestions: currentSuggestedQuestions,
      responseMode:
        historiesState?.currentHistory?.responseMode ||
        currentAgent?.responseMode ||
        templateValues?.responseMode ||
        DEFAULT_AGENT_RESPONSE_MODE,
      humanHandoff: historiesState?.currentHistory?.humanHandoff ||
        currentAgent?.humanHandoff ||
        templateValues?.humanHandoff || { ...DEFAULT_HUMAN_HANDOFF_CONFIG },
      aiTakeoverGreeting: normalizeAiTakeoverGreeting(
        // Use explicit branches (not `||`) so a cleared greeting stays cleared.
        currentAgent &&
          historiesState?.currentHistory?.agentId === currentAgent.id
          ? historiesState.currentHistory.aiTakeoverGreeting
          : currentAgent
            ? currentAgent.aiTakeoverGreeting
            : templateValues?.aiTakeoverGreeting
      ),
      outputPresentationPolicy: normalizeOutputPresentationPolicy(
        historiesState?.currentHistory?.outputPresentationPolicy ||
          currentAgent?.outputPresentationPolicy ||
          currentAgent?.meta?.outputPresentationPolicy ||
          templateValues?.outputPresentationPolicy
      ),
      pendingKnowledgeBindings: templateValues?.pendingKnowledgeBindings || [],
    }
    const nextResetSignature = getFormResetSignature(nextResetValues)

    if (lastResetSignatureRef.current === nextResetSignature) {
      isInitialLoadRef.current = false
      return
    }

    form.reset(nextResetValues, {
      keepDefaultValues: !!templateValues,
    })
    lastResetSignatureRef.current = nextResetSignature
    // Initialize tracking baseline to the loaded agent's system prompt
    setTrackingBaselineValue(initialSystemPrompt)
    // Use microtask to ensure form state is updated without arbitrary delay
    const finalizeReset = () => {
      isInitialLoadRef.current = false
      lastAppliedHistoryIdRef.current = currentAgent
        ? historiesState?.currentHistory?.id || null
        : null
    }

    if (typeof queueMicrotask === "function") {
      queueMicrotask(finalizeReset)
    } else {
      setTimeout(finalizeReset, 0)
    }
    // We intentionally exclude historiesState?.currentHistory?.attachments from dependencies
    // because including it would cause the form to reset every time attachments change,
    // which breaks the agent history switching functionality
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAgent, form, initialValues, systemDefaultLLMModel])

  useEffect(() => {
    const currentValue = form.getValues("llmModel")
    if (
      !attachmentsHaveAudio &&
      systemDefaultLLMModel &&
      (!currentValue || currentValue.trim() === "") &&
      !currentAgent?.llmModel
    ) {
      form.setValue("llmModel", systemDefaultLLMModel, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      })
    }
  }, [
    form,
    systemDefaultLLMModel,
    currentAgent?.llmModel,
    attachmentsHaveAudio,
  ])

  useEffect(() => {
    if (!attachmentsHaveAudio) return
    if (currentAgent?.llmModel) return
    if (historiesState?.currentHistory?.llmModel) return
    if (form.getValues("llmModel") !== systemDefaultLLMModel) return

    form.setValue("llmModel", "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    })
  }, [
    attachmentsHaveAudio,
    currentAgent?.llmModel,
    historiesState?.currentHistory?.llmModel,
    form,
    systemDefaultLLMModel,
  ])

  const handleConfirmClick = (formData: BasicFormType) => {
    if (audioAttachmentModelError) {
      form.setError("llmModel", {
        type: "manual",
        message: audioAttachmentModelError,
      })
      return
    }
    form.clearErrors("llmModel")

    // Extract knowledgeNodeIds from unified tools (only knowledge base tools)
    const knowledgeNodeIds = Array.from(
      new Set(
        formData.unifiedTools
          .filter((tool) => tool.type === "knowledge_base")
          .flatMap((tool) => {
            if (tool.knowledgeNodeIds && tool.knowledgeNodeIds.length > 0) {
              return tool.knowledgeNodeIds.map(String)
            }
            return []
          })
      )
    )

    // Convert form data to API format (extract attachment IDs and derived knowledgeNodeIds)
    const apiData: BasicFormType = {
      ...formData,
      knowledgeNodeIds,
      unifiedTools: formData.unifiedTools,
    }

    pendingSubmittedSignatureRef.current = getFormResetSignature(apiData)
    onConfirm?.(apiData)
  }

  // Copilot: register apply handlers and peekers for fields
  useEffect(() => {
    if (!copilotApplyEnabled) return

    const handleApplyName = async (raw: string) => {
      setValue("name", String(raw ?? ""))
      return true
    }
    const handleApplyDescription = async (raw: string) => {
      setValue("description", String(raw ?? ""))
      return true
    }
    const handleApplySystemPrompt = async (raw: string) => {
      setValue("systemPrompt", String(raw ?? ""))
      return true
    }
    const handleApplyLLMModel = makeLLMModelApplyHandler({
      modelOptions: modelOptionsEnsuringCurrentIncluded.map((m) => m.modelId),
      onApply: (value) => setValue("llmModel", value),
    })
    const handleApplyResponseMode = async (raw: string) => {
      const parsed = BasicFormSchema.shape.responseMode.safeParse(
        String(raw ?? "").trim()
      )
      if (!parsed.success) return false
      setValue("responseMode", parsed.data)
      return true
    }
    const handleApplyUnifiedTools = makeUnifiedToolsApplyHandler({
      knowledgeBases: Array.from(knowledgeBaseMap.values()),
      agents: availableAgentsForCallAgentTool,
      tabularToolsEnabled,
      getCurrentTools: () => getValues("unifiedTools"),
      onApply: (tools) => setValue("unifiedTools", tools),
    })
    const cleanup = registerFieldApplyEntries({
      [BASIC_FORM_APPLY_KEY_BY_FIELD_NAME.name]: {
        handler: handleApplyName,
        peek: () => getValues("name"),
      },
      [BASIC_FORM_APPLY_KEY_BY_FIELD_NAME.description]: {
        handler: handleApplyDescription,
        peek: () => getValues("description"),
      },
      [BASIC_FORM_APPLY_KEY_BY_FIELD_NAME.systemPrompt]: {
        handler: handleApplySystemPrompt,
        peek: () => getValues("systemPrompt"),
      },
      [BASIC_FORM_APPLY_KEY_BY_FIELD_NAME.llmModel]: {
        handler: handleApplyLLMModel,
        peek: () => getValues("llmModel"),
      },
      [BASIC_FORM_APPLY_KEY_BY_FIELD_NAME.responseMode]: {
        handler: handleApplyResponseMode,
        peek: () => getValues("responseMode"),
      },
      [BASIC_FORM_APPLY_KEY_BY_FIELD_NAME.unifiedTools]: {
        handler: handleApplyUnifiedTools,
        peek: () => getValues("unifiedTools"),
      },
    })
    return cleanup
  }, [
    copilotApplyEnabled,
    setValue,
    knowledgeBaseMap,
    availableAgentsForCallAgentTool,
    getValues,
    modelOptionsEnsuringCurrentIncluded,
    tabularToolsEnabled,
  ])

  const handleInstructionsModalOpenChange = (open: boolean) => {
    setIsInstructionsModalOpen(open)
  }

  const openInstructionsModal = () => handleInstructionsModalOpenChange(true)
  const closeInstructionsModal = () => handleInstructionsModalOpenChange(false)

  if (!knowledgeBaseMap) return null
  return (
    <Form {...form}>
      <FileUploadCollector
        ref={fileUploadRef}
        workspace={workspace}
        files={attachments}
        onStateChange={(newFiles) => {
          setValue("attachments", newFiles)
        }}
        showDragOverlay={true}
        maxFiles={MAX_ATTACHMENTS}
      >
        <form
          id={formId}
          className="flex flex-col gap-6"
          onSubmit={form.handleSubmit(handleConfirmClick)}
        >
          <div className="space-y-6 rounded-xl border border-border bg-background p-4 shadow-xs">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center justify-between pt-1">
                    <span>{t("workspace.agent.basicForm.name")}</span>
                    <div className="flex items-center gap-2">
                      {historiesState && (
                        <AgentHistoryDropdown
                          agentId={currentAgent?.id || ""}
                          state={historiesState}
                          disabled={
                            !currentAgent ||
                            !historiesState.histories ||
                            historiesState.histories.length === 0
                          }
                        />
                      )}
                    </div>
                  </FormLabel>
                  {/* Snapshot helpers (hidden): version selector current and options */}
                  {historiesState?.histories &&
                    historiesState.histories.length > 0 && (
                      <>
                        <CopilotHiddenField
                          fieldKey="toolbar.versionSelector.selected"
                          value={
                            historiesState.currentHistory?.versionNumber
                              ? `v${historiesState.currentHistory.versionNumber}`
                              : ""
                          }
                        />
                        <CopilotHiddenField
                          fieldKey="toolbar.versionSelector.options"
                          value={historiesState.histories
                            .map((h) =>
                              h.versionNumber ? `v${h.versionNumber}` : ""
                            )
                            .filter(Boolean)
                            .join(" | ")}
                        />
                      </>
                    )}
                  <FormControl>
                    <Input
                      maxLength={256}
                      data-copilot-field="basicForm.name"
                      {...field}
                    />
                  </FormControl>
                  <FormErrorMessage
                    message={t("workspace.agent.basicForm.error.name")}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      {t("workspace.agent.basicForm.description")}
                      <FieldHelpTooltip
                        message={t(
                          "workspace.agent.basicForm.descriptionHelp",
                          "Offer a short overview so teammates know this agent’s purpose and when to rely on it."
                        )}
                      />
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      maxLength={512}
                      className="min-h-[60px]"
                      data-copilot-field="basicForm.description"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div ref={systemPromptSectionRef}>
              <FormField
                control={form.control}
                name="systemPrompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        {t("workspace.agent.basicForm.systemPrompt")}
                        <FieldHelpTooltip
                          message={t(
                            "workspace.agent.basicForm.systemPromptHelp",
                            "Spell out the playbook the agent must follow—tone, guardrails, and step-by-step actions."
                          )}
                        />
                      </span>
                      <div className="flex items-center gap-1.5">
                        {hasSystemPromptChanges ? (
                          <Button
                            type="button"
                            size="sm"
                            variant={showTrackChanges ? "secondary" : "ghost"}
                            className="h-8 gap-1.5 px-2 text-xs"
                            onClick={() =>
                              setShowTrackChanges((current) => !current)
                            }
                            aria-pressed={showTrackChanges}
                          >
                            {showTrackChanges ? (
                              <PencilLine className="h-3.5 w-3.5" />
                            ) : (
                              <GitCompareArrows className="h-3.5 w-3.5" />
                            )}
                            {t(
                              showTrackChanges
                                ? "workspace.agent.basicForm.continueEditing"
                                : "workspace.agent.trackChanges"
                            )}
                            {historiesState?.currentHistory?.versionNumber ? (
                              <span className="rounded bg-background/80 px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
                                {t(
                                  "workspace.agent.basicForm.comparedWithVersion",
                                  {
                                    version:
                                      historiesState.currentHistory
                                        .versionNumber,
                                  }
                                )}
                              </span>
                            ) : null}
                          </Button>
                        ) : null}
                        <TooltipProvider delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={openInstructionsModal}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                aria-label={t(
                                  "workspace.agent.basicForm.instructionsFocus",
                                  "Focus editor"
                                )}
                              >
                                <Maximize2 className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="left">
                              <p>
                                {t(
                                  "workspace.agent.basicForm.instructionsFocusTooltip",
                                  "Open a focused editor"
                                )}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </FormLabel>
                    <FormControl>
                      <AITrackedTextarea
                        {...field}
                        defaultValue={field.value}
                        className="min-h-[220px]"
                        textareaClassName="min-h-[220px]"
                        rows={8}
                        originalValue={trackingBaselineValue}
                        data-copilot-field="basicForm.systemPrompt"
                        showTracking={showTrackChanges}
                        placeholder={t(
                          "workspace.agent.basicForm.systemPromptPlaceholder"
                        )}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="llmModel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      {t("workspace.agent.basicForm.llmModel", "LLM Model")}
                      <FieldHelpTooltip
                        message={t(
                          "workspace.agent.basicForm.llmModelHelp",
                          "Choose the foundation model. Each option shifts capability, tone, and speed, so try a few."
                        )}
                      />
                    </span>
                  </FormLabel>
                  <LLMModelSelect
                    models={llmModelOptions}
                    value={field.value}
                    onValueChange={(value) => {
                      form.clearErrors("llmModel")
                      field.onChange(value)
                    }}
                    placeholder={t(
                      "workspace.agent.basicForm.llmModelPlaceholder",
                      "Select model"
                    )}
                    testId="llm-model-select"
                    copilotField="basicForm.llmModel"
                    pricingNote={t(
                      "workspace.agent.basicForm.llmModelPricingNote",
                      "Actual cost is based on token usage"
                    )}
                  />
                  {isCurrentModelMissing && (
                    <p className="text-sm text-amber-600">
                      {t("workspace.agent.basicForm.llmModelMissingWarning")}
                    </p>
                  )}
                  {audioAttachmentModelError && (
                    <p className="text-sm text-destructive">
                      {audioAttachmentModelError}
                    </p>
                  )}
                  <FormMessage />
                  {/* Snapshot options for Copilot (hidden, plain list) */}
                  <CopilotHiddenField
                    fieldKey="basicForm.llmModel.options"
                    value={llmModelOptions.map((m) => m.modelId).join(" | ")}
                  />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-4 rounded-xl border border-border bg-muted/40 p-4 dark:bg-muted/20">
            <CopilotHiddenField
              fieldKey="basicForm.unifiedTools"
              value={JSON.stringify(unifiedTools || [])}
            />
            <CopilotHiddenField
              fieldKey="basicForm.unifiedTools.tabularData.enabled"
              value={String(tabularToolsEnabled)}
            />
            <FormField
              control={form.control}
              name="unifiedTools"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      {t("workspace.agent.basicForm.tools")}
                      <FieldHelpTooltip
                        message={t(
                          "workspace.agent.basicForm.toolsHelp",
                          "Select the data sources and helper actions this agent can call, from research tools to fellow agents."
                        )}
                      />
                    </span>
                  </FormLabel>
                  <FormControl>
                    <div>
                      <UnifiedToolSelector
                        knowledgeBases={Array.from(knowledgeBaseMap.values())}
                        agents={availableAgentsForCallAgentTool}
                        selectedTools={field.value || []}
                        onToolsChange={field.onChange}
                        disabled={form.formState.disabled}
                        pendingKnowledgeBindings={pendingKnowledgeBindings}
                        onPendingKnowledgeBindingsChange={(bindings) =>
                          form.setValue("pendingKnowledgeBindings", bindings, {
                            shouldDirty: true,
                          })
                        }
                        onTabularToolsEnabledChange={setTabularToolsEnabled}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Accordion
            type="single"
            collapsible
            value={advancedValue}
            onValueChange={setAdvancedValue}
            className="rounded-xl border border-border bg-muted/40 px-4 dark:bg-muted/20"
          >
            <AccordionItem value="advanced" className="border-0">
              <AccordionTrigger className="hover:no-underline">
                <span
                  className="hidden"
                  data-copilot-field="basicForm.advanced"
                  data-copilot-value={JSON.stringify({
                    expanded: advancedValue === "advanced",
                    summary: advancedSummary,
                  })}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1 pr-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span>
                      {t(
                        "workspace.agent.basicForm.sections.advanced",
                        "Advanced"
                      )}
                    </span>
                    {hasAdvancedErrors && (
                      <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                        {t(
                          "workspace.agent.basicForm.advancedNeedsAttention",
                          "Needs attention"
                        )}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-normal text-muted-foreground">
                    {advancedSummary}
                  </span>
                </div>
              </AccordionTrigger>
              <CopilotHiddenField
                fieldKey="basicForm.attachments"
                value={summarizeAttachments(attachments)}
              />
              <CopilotHiddenField
                fieldKey="basicForm.responseMode"
                value={responseMode}
              />
              <AccordionContent className="space-y-6 pb-4">
                {llmModel && (
                  <div className="space-y-4 rounded-lg border border-border bg-background/70 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {t(
                            "workspace.agent.basicForm.modelSettings",
                            "Model Settings"
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t(
                            "workspace.agent.basicForm.modelSettingsDescription",
                            "These settings apply only to the currently selected model. Leave blank to use the model default values."
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            "workspace.agent.basicForm.modelSettingsSamplingHint",
                            "Temperature and Top P are best used one at a time."
                          )}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        data-testid="llm-model-settings-reset"
                        className="self-start text-xs sm:text-sm"
                        disabled={!hasCurrentLLMModelSettings}
                        onClick={() =>
                          updateCurrentLLMModelSettings(() => ({}))
                        }
                      >
                        {t(
                          "workspace.agent.basicForm.modelSettingsReset",
                          "Reset to defaults"
                        )}
                      </Button>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormItem>
                        <FormLabel>
                          {t(
                            "workspace.agent.basicForm.modelSettingsTemperature",
                            "Temperature"
                          )}
                        </FormLabel>
                        <FormControl>
                          <Input
                            data-testid="llm-temperature-input"
                            type="number"
                            min={0}
                            max={2}
                            step="0.1"
                            placeholder={t(
                              "workspace.agent.basicForm.modelSettingsNumericPlaceholder",
                              "Use model default"
                            )}
                            value={currentLLMModelSettings?.temperature ?? ""}
                            onChange={(event) => {
                              const rawValue = event.target.value
                              updateCurrentLLMModelSettings((settings) => ({
                                ...settings,
                                temperature:
                                  rawValue === ""
                                    ? undefined
                                    : Number(rawValue),
                              }))
                            }}
                          />
                        </FormControl>
                      </FormItem>

                      <FormItem>
                        <FormLabel>
                          {t(
                            "workspace.agent.basicForm.modelSettingsMaxTokens",
                            "Max Tokens"
                          )}
                        </FormLabel>
                        <FormControl>
                          <Input
                            data-testid="llm-max-tokens-input"
                            type="number"
                            min={1}
                            step="1"
                            placeholder={t(
                              "workspace.agent.basicForm.modelSettingsNumericPlaceholder",
                              "Use model default"
                            )}
                            value={currentLLMModelSettings?.maxTokens ?? ""}
                            onChange={(event) => {
                              const rawValue = event.target.value
                              updateCurrentLLMModelSettings((settings) => ({
                                ...settings,
                                maxTokens:
                                  rawValue === ""
                                    ? undefined
                                    : Number(rawValue),
                              }))
                            }}
                          />
                        </FormControl>
                      </FormItem>

                      <FormItem>
                        <FormLabel>
                          {t(
                            "workspace.agent.basicForm.modelSettingsTopP",
                            "Top P"
                          )}
                        </FormLabel>
                        <FormControl>
                          <Input
                            data-testid="llm-top-p-input"
                            type="number"
                            min={0.01}
                            max={1}
                            step="0.01"
                            placeholder={t(
                              "workspace.agent.basicForm.modelSettingsNumericPlaceholder",
                              "Use model default"
                            )}
                            value={currentLLMModelSettings?.topP ?? ""}
                            onChange={(event) => {
                              const rawValue = event.target.value
                              updateCurrentLLMModelSettings((settings) => ({
                                ...settings,
                                topP:
                                  rawValue === ""
                                    ? undefined
                                    : Number(rawValue),
                              }))
                            }}
                          />
                        </FormControl>
                      </FormItem>

                      <FormItem>
                        <FormLabel>
                          {t(
                            "workspace.agent.basicForm.modelSettingsThinking",
                            "Thinking Level"
                          )}
                        </FormLabel>
                        <Select
                          data-testid="llm-thinking-select"
                          value={
                            currentLLMModelSettings?.thinking ?? "__default__"
                          }
                          onValueChange={(value) => {
                            updateCurrentLLMModelSettings((settings) => ({
                              ...settings,
                              thinking:
                                value === "__default__"
                                  ? undefined
                                  : (value as AgentThinkingLevel),
                            }))
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={t(
                                "workspace.agent.basicForm.modelSettingsThinkingPlaceholder",
                                "Default"
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {THINKING_OPTIONS.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {t(
                                  `workspace.agent.basicForm.modelSettingsThinkingOptions.${option.value === "__default__" ? "default" : option.value}`,
                                  option.label
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    </div>

                    {extractLLMModelSettingsErrorMessages(
                      currentLLMModelSettingsErrors
                    ).map((msg) => (
                      <p key={msg} className="text-sm text-destructive">
                        {msg}
                      </p>
                    ))}
                    {otherLLMModelSettingsErrorMessages.map((message) => (
                      <p key={message} className="text-sm text-destructive">
                        {message}
                      </p>
                    ))}
                  </div>
                )}

                <div className="space-y-4 rounded-lg border border-border bg-background/70 p-4">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium leading-none">
                        {t(
                          "workspace.agent.basicForm.outputPresentation.title",
                          "Output presentation"
                        )}
                      </label>
                      <FieldHelpTooltip
                        message={t(
                          "workspace.agent.basicForm.outputPresentation.help",
                          "Controls whether the agent may add validated visual presentation components after its normal text answer."
                        )}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "workspace.agent.basicForm.outputPresentation.description",
                        "Verified citations use server-checked source references. Cards, galleries, locations, lists, status timelines, and action panels are model-organized presentation, not verified source records. Output presentations currently display only on LINE; other channels keep the text answer without these visual components."
                      )}
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="outputPresentationPolicy.enableOutputPresentations"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-md border border-border p-3">
                        <div className="space-y-1">
                          <FormLabel className="text-sm font-medium">
                            {t(
                              "workspace.agent.basicForm.outputPresentation.enableLabel",
                              "Enable output presentation"
                            )}
                          </FormLabel>
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "workspace.agent.basicForm.outputPresentation.enableHelp",
                              "When disabled, the agent answers with text and cannot request output presentation components."
                            )}
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            data-testid="output-presentation-enable"
                            checked={field.value}
                            onCheckedChange={(checked) => {
                              field.onChange(checked)
                              updateOutputPresentationPolicy((policy) => ({
                                ...policy,
                                enableOutputPresentations: checked,
                              }))
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {outputPresentationPolicy?.enableOutputPresentations && (
                    <div className="space-y-3">
                      {OUTPUT_PRESENTATION_KINDS.map(
                        ({ kind, defaultMaxItems, maxItemLimit }) => {
                          const enabled =
                            outputPresentationPolicy.allowedPresentationKinds.includes(
                              kind
                            )
                          const maxItems =
                            outputPresentationPolicy.maxItemsByKind[kind] ??
                            defaultMaxItems
                          const kindLabel = t(
                            `workspace.agent.basicForm.outputPresentation.kinds.${kind}.label`
                          )
                          return (
                            <div
                              key={kind}
                              className="space-y-3 rounded-md border border-border p-3"
                            >
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  id={`output-presentation-${kind}`}
                                  data-testid={`output-presentation-kind-${kind}`}
                                  checked={enabled}
                                  onCheckedChange={(checked) =>
                                    setOutputPresentationKindEnabled(
                                      kind,
                                      checked === true
                                    )
                                  }
                                />
                                <div className="min-w-0 flex-1 space-y-1">
                                  <div className="flex min-w-0 items-center gap-1.5">
                                    <label
                                      htmlFor={`output-presentation-${kind}`}
                                      className="block min-w-0 text-sm font-medium leading-none"
                                    >
                                      {kindLabel}
                                    </label>
                                    <OutputPresentationPreviewTooltip
                                      kind={kind}
                                      label={kindLabel}
                                      previewLabel={t(
                                        "workspace.agent.basicForm.outputPresentation.previewLabel",
                                        {
                                          kind: kindLabel,
                                          defaultValue:
                                            "Preview {{kind}} presentation",
                                        }
                                      )}
                                    />
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {t(
                                      `workspace.agent.basicForm.outputPresentation.kinds.${kind}.description`
                                    )}
                                    {maxItemLimit === 1 && (
                                      <>
                                        {" "}
                                        {t(
                                          "workspace.agent.basicForm.outputPresentation.fixedSingleComponentDescription",
                                          "This presentation is fixed to 1 component."
                                        )}
                                      </>
                                    )}
                                  </p>
                                </div>
                              </div>

                              {enabled && (
                                <div
                                  className={
                                    maxItemLimit === 1
                                      ? "grid gap-3"
                                      : "grid gap-3 sm:grid-cols-[5rem_minmax(0,1fr)] sm:items-start"
                                  }
                                >
                                  {maxItemLimit > 1 && (
                                    <FormItem className="gap-2">
                                      <FormLabel className="flex h-5 items-center">
                                        {t(
                                          "workspace.agent.basicForm.outputPresentation.maxItems",
                                          "Max items"
                                        )}
                                      </FormLabel>
                                      <Input
                                        data-testid={`output-presentation-max-${kind}`}
                                        className="w-20"
                                        type="number"
                                        min={1}
                                        max={maxItemLimit}
                                        step={1}
                                        value={maxItems}
                                        onChange={(event) => {
                                          const rawValue = Number(
                                            event.target.value || 0
                                          )
                                          const nextValue = Math.max(
                                            0,
                                            Math.min(maxItemLimit, rawValue)
                                          )
                                          const editingPolicy =
                                            outputPresentationPolicy ||
                                            DEFAULT_OUTPUT_PRESENTATION_POLICY
                                          setValue(
                                            "outputPresentationPolicy",
                                            {
                                              ...editingPolicy,
                                              maxItemsByKind: {
                                                ...editingPolicy.maxItemsByKind,
                                                [kind]: nextValue,
                                              },
                                            },
                                            {
                                              shouldDirty: true,
                                              shouldTouch: true,
                                              shouldValidate: false,
                                            }
                                          )
                                        }}
                                        onBlur={() => {
                                          updateOutputPresentationPolicy(
                                            (policy) => ({
                                              ...policy,
                                              maxItemsByKind: {
                                                ...policy.maxItemsByKind,
                                                [kind]:
                                                  clampOutputPresentationMaxItems(
                                                    kind,
                                                    policy.maxItemsByKind[kind]
                                                  ),
                                              },
                                            })
                                          )
                                        }}
                                      />
                                    </FormItem>
                                  )}
                                  <FormItem className="gap-2">
                                    <FormLabel className="flex h-5 items-center">
                                      {t(
                                        "workspace.agent.basicForm.outputPresentation.guidanceLabel",
                                        "Additional guidance"
                                      )}
                                    </FormLabel>
                                    <Textarea
                                      data-testid={`output-presentation-guidance-${kind}`}
                                      rows={2}
                                      maxLength={
                                        MAX_PRESENTATION_GUIDANCE_LENGTH
                                      }
                                      value={
                                        outputPresentationPolicy
                                          .whenToUseByKind[kind] || ""
                                      }
                                      onChange={(event) =>
                                        updateOutputPresentationPolicy(
                                          (policy) => ({
                                            ...policy,
                                            whenToUseByKind: {
                                              ...policy.whenToUseByKind,
                                              [kind]: event.target.value,
                                            },
                                          })
                                        )
                                      }
                                      placeholder={t(
                                        "workspace.agent.basicForm.outputPresentation.guidancePlaceholder",
                                        "Optional advisory guidance for when the agent should consider this presentation."
                                      )}
                                    />
                                  </FormItem>
                                </div>
                              )}
                            </div>
                          )
                        }
                      )}
                      <p className="text-xs text-muted-foreground">
                        {t(
                          "workspace.agent.basicForm.outputPresentation.guidanceHelp",
                          "Guidance helps the agent decide when to use a presentation, but it does not guarantee output and cannot override disabled kinds, limits, schemas, provider restrictions, or trust boundaries."
                        )}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-3 rounded-lg border border-border bg-background/70 p-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {t("workspace.agent.basicForm.attachments")}
                      <span className="text-sm text-gray-500 ml-2">
                        ({(form.getValues("attachments") || []).length}/
                        {MAX_ATTACHMENTS})
                      </span>
                    </label>
                    <FieldHelpTooltip
                      message={t(
                        "workspace.agent.basicForm.attachmentsHelp",
                        "Uploaded files ride along with every request so the agent can quote them without re-uploading."
                      )}
                    />
                  </div>
                  <AttachmentCollector
                    className="w-full"
                    workspace={workspace}
                    withIcon={true}
                    initialFiles={attachments}
                    maxAttachments={MAX_ATTACHMENTS}
                    label={t("workspace.agent.basicForm.attachmentsAdd")}
                    onAttachmentsChange={(newFiles) => {
                      setValue("attachments", newFiles)
                    }}
                  />
                </div>

                {/* Suggested Questions */}
                <div className="space-y-3 rounded-lg border border-border bg-background/70 p-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {t(
                        "workspace.agent.basicForm.suggestedQuestions",
                        "Suggested Questions"
                      )}
                      <span className="text-sm text-gray-500 ml-2">
                        ({(suggestedQuestions || []).length}/
                        {MAX_SUGGESTED_QUESTIONS})
                      </span>
                    </label>
                    <FieldHelpTooltip
                      message={t(
                        "workspace.agent.basicForm.suggestedQuestionsHelp",
                        "Provide sample questions to help users get started quickly."
                      )}
                    />
                  </div>
                  <SuggestedQuestionsField
                    questions={suggestedQuestions || []}
                    onQuestionsChange={(newQuestions) => {
                      setValue("suggestedQuestions", newQuestions)
                    }}
                    disabled={form.formState.disabled}
                  />
                </div>

                <div className="space-y-4 rounded-lg border border-border bg-background p-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium leading-none">
                      {t(
                        "workspace.agent.basicForm.responseMode.title",
                        "Response mode"
                      )}
                    </label>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "workspace.agent.basicForm.responseMode.description",
                        "Choose whether external client messages are answered by AI or handled only by people."
                      )}
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="responseMode"
                    render={({ field }) => (
                      <FormItem>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="ai">
                              {t(
                                "workspace.agent.basicForm.responseMode.options.ai",
                                "AI replies"
                              )}
                            </SelectItem>
                            <SelectItem value="human_only">
                              {t(
                                "workspace.agent.basicForm.responseMode.options.humanOnly",
                                "Human replies only"
                              )}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground">
                          {responseMode === "human_only"
                            ? t(
                                "workspace.agent.basicForm.responseMode.humanOnlyHelp",
                                "External messages are saved for a person to answer. The agent will not send an automatic reply, handoff message, or handoff notification."
                              )
                            : t(
                                "workspace.agent.basicForm.responseMode.aiHelp",
                                "The agent answers normally and may hand off selected conversations when automatic handoff is enabled."
                              )}
                        </p>
                      </FormItem>
                    )}
                  />
                </div>

                {responseMode === "ai" ? (
                  <div className="space-y-4 rounded-lg border border-border bg-background p-4">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium leading-none">
                          {t(
                            "workspace.agent.basicForm.humanHandoff.title",
                            "Human Handoff"
                          )}
                        </label>
                        <FieldHelpTooltip
                          message={t(
                            "workspace.agent.basicForm.humanHandoff.help",
                            "Let the agent switch a client into human-only mode after it requests a handoff."
                          )}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "workspace.agent.basicForm.humanHandoff.description",
                          "When enabled, the agent can pause AI replies and route the user to a human for a limited time."
                        )}
                      </p>
                    </div>

                    <FormField
                      control={form.control}
                      name="humanHandoff.enabled"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between rounded-md border border-border p-3">
                          <div className="space-y-1">
                            <FormLabel className="text-sm font-medium">
                              {t(
                                "workspace.agent.basicForm.humanHandoff.enableLabel",
                                "Enable human handoff"
                              )}
                            </FormLabel>
                            <p className="text-sm text-muted-foreground">
                              {t(
                                "workspace.agent.basicForm.humanHandoff.enableHelp",
                                "Use this when the agent should stop answering and wait for a person to take over."
                              )}
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="humanHandoff.idleTimeoutMinutes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t(
                              "workspace.agent.basicForm.humanHandoff.idleTimeoutLabel",
                              "Idle timeout (minutes)"
                            )}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={field.value ?? ""}
                              onChange={(event) => {
                                const value = event.target.value.replace(
                                  /\D+/g,
                                  ""
                                )
                                if (value === "") {
                                  form.setValue(
                                    "humanHandoff.idleTimeoutMinutes",
                                    null,
                                    {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                      shouldValidate: true,
                                    }
                                  )
                                  return
                                }

                                field.onChange(Number(value))
                              }}
                              disabled={!humanHandoffEnabled}
                              placeholder={t(
                                "workspace.agent.basicForm.humanHandoff.idleTimeoutPlaceholder",
                                "e.g. 30"
                              )}
                            />
                          </FormControl>
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "workspace.agent.basicForm.humanHandoff.idleTimeoutHelp",
                              "After this many idle minutes, the user automatically returns to AI mode."
                            )}
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="humanHandoff.handoffInstructions"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t(
                              "workspace.agent.basicForm.humanHandoff.instructionsLabel",
                              "Handoff instructions"
                            )}
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              rows={4}
                              value={field.value || ""}
                              onChange={field.onChange}
                              disabled={!humanHandoffEnabled}
                              placeholder={t(
                                "workspace.agent.basicForm.humanHandoff.instructionsPlaceholder",
                                "Describe the situations where the agent should stop replying and hand the client to a human."
                              )}
                            />
                          </FormControl>
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "workspace.agent.basicForm.humanHandoff.instructionsHelp",
                              "This becomes part of the agent's runtime instructions so it knows when to trigger a human handoff."
                            )}
                          </p>
                        </FormItem>
                      )}
                    />

                    {currentAgent ? (
                      <HandoffNotificationSection
                        organizationId={organizationId}
                        workspaceId={workspace.id}
                        agentId={currentAgent.id}
                        humanHandoffEnabled={humanHandoffEnabled}
                        canManageHandoffNotifications={
                          canManageHandoffNotifications
                        }
                      />
                    ) : null}
                  </div>
                ) : null}

                {responseMode === "ai" ? (
                  <div className="space-y-4 rounded-lg border border-border bg-background p-4">
                    <FormField
                      control={form.control}
                      name="aiTakeoverGreeting"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <FormLabel className="text-sm font-medium leading-none">
                                {t(
                                  "workspace.agent.basicForm.aiTakeoverGreeting.label",
                                  "AI takeover greeting (optional)"
                                )}
                              </FormLabel>
                              <FieldHelpTooltip
                                message={t(
                                  "workspace.agent.basicForm.aiTakeoverGreeting.help",
                                  "Lets clients know the AI agent is answering, including when it takes over from a person."
                                )}
                              />
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {t(
                                "workspace.agent.basicForm.aiTakeoverGreeting.description",
                                "Sent exactly as written before the first AI reply, and once again when AI resumes after human handling or a handoff timeout. Ongoing chats do not repeat it. Leave blank to send no greeting."
                              )}
                            </p>
                          </div>
                          <FormControl>
                            <Textarea
                              data-testid="ai-takeover-greeting-input"
                              data-copilot-field="basicForm.aiTakeoverGreeting"
                              rows={3}
                              maxLength={MAX_AI_TAKEOVER_GREETING_LENGTH}
                              aria-invalid={fieldState.error ? true : undefined}
                              name={field.name}
                              ref={field.ref}
                              value={field.value ?? ""}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              disabled={form.formState.disabled}
                              placeholder={t(
                                "workspace.agent.basicForm.aiTakeoverGreeting.placeholder",
                                "e.g. Hi, this is the AI assistant again. I'll keep helping you from here."
                              )}
                            />
                          </FormControl>
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-xs text-muted-foreground">
                              {t(
                                "workspace.agent.basicForm.aiTakeoverGreeting.publishHint",
                                "Saving creates a draft version. Clients get the new greeting only after you publish that version."
                              )}
                            </p>
                            <span
                              className="shrink-0 text-xs tabular-nums text-muted-foreground"
                              data-testid="ai-takeover-greeting-count"
                            >
                              {(field.value || "").length}/
                              {MAX_AI_TAKEOVER_GREETING_LENGTH}
                            </span>
                          </div>
                          {fieldState.error ? (
                            <p
                              className="text-sm text-destructive"
                              role="alert"
                            >
                              {t(
                                "workspace.agent.basicForm.aiTakeoverGreeting.tooLong",
                                {
                                  max: MAX_AI_TAKEOVER_GREETING_LENGTH,
                                  defaultValue:
                                    "Shorten the greeting to {{max}} characters or fewer, then save again.",
                                }
                              )}
                            </p>
                          ) : null}
                        </FormItem>
                      )}
                    />
                  </div>
                ) : null}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {children}
        </form>
      </FileUploadCollector>

      {/* History Change Warning Dialog */}
      <UnsavedChangesWarning
        open={showHistoryChangeWarning}
        onOpenChange={setShowHistoryChangeWarning}
        onConfirm={handleHistoryChangeConfirm}
        onCancel={handleHistoryChangeCancel}
      />

      <Dialog
        open={isInstructionsModalOpen}
        onOpenChange={handleInstructionsModalOpenChange}
      >
        <DialogContent className="sm:max-w-[900px] h-[80vh] flex flex-col [&>button.absolute]:hidden">
          <DialogHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <DialogTitle>
                {t(
                  "workspace.agent.basicForm.instructionsModalTitle",
                  "Edit Instructions"
                )}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "workspace.agent.basicForm.instructionsModalDescription",
                  "Focus on long-form instructions in a distraction-free space."
                )}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-4">
              <DialogClose asChild>
                <Button variant="ghost" size="icon">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </DialogClose>
            </div>
          </DialogHeader>
          <div className="flex-1 py-4 min-h-[400px]">
            <div className="h-full rounded-md border border-border overflow-hidden">
              <CodeMirror
                value={systemPrompt || ""}
                height="100%"
                style={{ height: "100%" }}
                basicSetup={{
                  lineNumbers: false,
                  highlightActiveLine: false,
                  foldGutter: false,
                }}
                extensions={[markdown(), EditorView.lineWrapping]}
                theme={oneDark}
                onChange={(value) => {
                  setValue("systemPrompt", value, {
                    shouldDirty: true,
                    shouldTouch: true,
                  })
                }}
                aria-label={t(
                  "workspace.agent.basicForm.systemPrompt",
                  "Instructions"
                )}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="button" onClick={closeInstructionsModal}>
              {t("workspace.agent.basicForm.doneEditing", "Done")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Form>
  )
}

export default BasicForm
